import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Badge,
  Chip,
  Paper,
  Divider
} from '@mui/material';
import {
  Group as GroupIcon,
  Event as EventIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { ChatThread, ThreadParticipant } from '../../utils/api';

interface ChatListProps {
  threads: ChatThread[];
  participants: { [threadId: string]: ThreadParticipant[] };
  selectedThreadId?: string;
  onThreadSelect: (threadId: string) => void;
  loading?: boolean;
}

const ChatList: React.FC<ChatListProps> = ({
  threads,
  participants,
  selectedThreadId,
  onThreadSelect,
  loading = false
}) => {
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } else if (diffInHours < 168) { // Less than a week
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const getEventIcon = (activityType?: string) => {
    // You can customize this based on activity type
    return <EventIcon sx={{ fontSize: 20 }} />;
  };

  const getScopeColor = (scope: string) => {
    return scope === 'booking' ? '#4caf50' : '#ff9800';
  };

  const getScopeLabel = (scope: string) => {
    return scope === 'booking' ? 'Active' : 'Pending';
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">Loading chats...</Typography>
      </Box>
    );
  }

  if (threads.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <GroupIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No chats yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          When you join events, your chats will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ height: '100%', overflow: 'hidden' }}>
      <List sx={{ p: 0, height: '100%', overflow: 'auto' }}>
        {threads.map((thread, index) => {
          const threadParticipants = participants[thread.id] || [];
          const isSelected = selectedThreadId === thread.id;
          
          return (
            <React.Fragment key={thread.id}>
              <ListItem
                button
                selected={isSelected}
                onClick={() => onThreadSelect(thread.id)}
                sx={{
                  py: 2,
                  px: 2,
                  '&.Mui-selected': {
                    backgroundColor: '#533B7D20',
                    borderRight: '3px solid #533B7D'
                  },
                  '&:hover': {
                    backgroundColor: '#533B7D10'
                  }
                }}
              >
                <ListItemAvatar>
                  <Badge
                    badgeContent={thread.unread_count > 0 ? thread.unread_count : null}
                    color="error"
                    max={99}
                  >
                    <Avatar
                      sx={{
                        backgroundColor: '#533B7D',
                        width: 48,
                        height: 48
                      }}
                    >
                      {getEventIcon(thread.event?.activity_type)}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>

                <ListItemText
                  sx={{ ml: 1 }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: thread.unread_count > 0 ? 'bold' : 'normal',
                          color: '#333',
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {thread.event?.title || 'Event Chat'}
                      </Typography>
                      <Chip
                        label={getScopeLabel(thread.scope)}
                        size="small"
                        sx={{
                          backgroundColor: getScopeColor(thread.scope),
                          color: 'white',
                          fontSize: '0.7rem',
                          height: 20
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      {/* Last message preview */}
                      {thread.last_message && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mb: 0.5,
                            fontWeight: thread.unread_count > 0 ? 'bold' : 'normal'
                          }}
                        >
                          {thread.last_message.kind === 'system' ? (
                            <em>{thread.last_message.body}</em>
                          ) : (
                            `${thread.last_message.sender_name || 'Someone'}: ${thread.last_message.body}`
                          )}
                        </Typography>
                      )}
                      
                      {/* Event details and time */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <GroupIcon sx={{ fontSize: 14, color: '#666' }} />
                          <Typography variant="caption" color="text.secondary">
                            {thread.participant_count} participant{thread.participant_count !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                        
                        <Typography variant="caption" color="text.secondary">
                          •
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimeIcon sx={{ fontSize: 14, color: '#666' }} />
                          <Typography variant="caption" color="text.secondary">
                            {thread.last_message 
                              ? formatTime(thread.last_message.created_at)
                              : formatTime(thread.created_at)
                            }
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
              
              {index < threads.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          );
        })}
      </List>
    </Paper>
  );
};

export default ChatList;
